import { atom } from 'jotai';
import type { SubsurfaceColorRule } from './color-rule';
import { DEFAULT_RASTER_OPACITY_PERCENT } from './utils';
import type { SubsurfaceMapRow } from './types';

/** Where the shown map's cells are in their lifecycle. */
type RasterStatus = 'hidden' | 'loading' | 'shown';

/**
 * The one selected subsurface map (the row's locator), or null. Only one is
 * selected at a time: selecting another replaces it, and selecting it again clears it.
 */
export const selectedSubsurfaceMapAtom = atom<SubsurfaceMapRow | null>(null);

/**
 * The one colour rule for every subsurface map, or null for none (each map then
 * paints in its own default ramp). Session-only in Epic 1: never saved, so a
 * refresh goes back to each map's default.
 */
export const subsurfaceColorRuleAtom = atom<SubsurfaceColorRule | null>(null);

/** The shown map's opacity, 0–100: 100 minus the modal's Transparency. */
export const subsurfaceOpacityPercentAtom = atom(
  DEFAULT_RASTER_OPACITY_PERCENT
);

/**
 * The sidebar colour row's Enable/Disable: disabling shows the selected map as a
 * grey extent instead of its coloured cells, keeping the selection for Enable.
 */
export const subsurfaceColoringEnabledAtom = atom(true);

/** Load state of the selected map's cells, for the locator and the toast. */
export const subsurfaceRasterStatusAtom = atom<RasterStatus>('hidden');

/**
 * The selected map's own min/max as its cells tiles carry it, or null. A map whose
 * stats aren't built yet is painted across this range, so the legend labels it.
 */
export const subsurfaceShownGridRangeAtom = atom<[number, number] | null>(null);

/**
 * The id of the map whose cells are actually on the map, or null: selected, loaded
 * and coloured. Its grey fill gives way to the cells only while this is set, so
 * the extent never vanishes while tiles load or after a Disable.
 */
export const subsurfaceRasterOnMapIdAtom = atom((get) => {
  const shown = get(selectedSubsurfaceMapAtom);
  if (!shown || get(subsurfaceRasterStatusAtom) !== 'shown') return null;
  if (!get(subsurfaceColoringEnabledAtom)) return null;
  return shown.id;
});

/** A row locator's state: idle, selected, or selected while its cells load. */
export type LocatorState = 'idle' | 'selected' | 'loading';

/**
 * One row's locator state, so selecting a map re-renders only the rows it changes.
 * @param {number} mapId The row's map id.
 * @return {Atom<LocatorState>} The derived state.
 */
export const locatorStateAtom = (mapId: number) =>
  atom<LocatorState>((get) => {
    if (get(selectedSubsurfaceMapAtom)?.id !== mapId) return 'idle';
    return get(subsurfaceRasterStatusAtom) === 'loading'
      ? 'loading'
      : 'selected';
  });

/**
 * The locator toggle: selects the row's map (drawing its cells), or clears the
 * selection when it is already selected. Selecting always shows the map coloured.
 */
export const toggleSelectedSubsurfaceMapAtom = atom(
  null,
  (get, set, row: SubsurfaceMapRow) => {
    const shown = get(selectedSubsurfaceMapAtom);
    set(selectedSubsurfaceMapAtom, shown?.id === row.id ? null : row);
    set(subsurfaceColoringEnabledAtom, true);
  }
);

/**
 * The footprints' fill opacity multiplier: none until a map is selected; then only
 * the selected map keeps its fill (until its cells load, and again while colouring
 * is disabled). The faded fills stay on the map, so every footprint keeps its hover
 * card and click.
 */
export const subsurfaceFillOpacityAtom = atom<unknown[] | null>((get) => {
  const shown = get(selectedSubsurfaceMapAtom);
  if (!shown) return null;
  if (get(subsurfaceRasterOnMapIdAtom) !== null) return ['literal', 0];
  return ['case', ['==', ['get', 'id'], shown.id], 1, 0];
});

/** What datasets without cells read in place of selectedSubsurfaceMapAtom. */
export const NO_SELECTED_SUBSURFACE_MAP = atom<SubsurfaceMapRow | null>(null);

/** What the Subsurface search and facets read as while nobody needs them. */
export const NO_SUBSURFACE_SEARCH = atom<{
  data?: { results?: SubsurfaceMapRow[] };
}>({});
export const NO_SUBSURFACE_FACETS = atom<{
  valueHistogram?: { min: number; max: number };
} | null>(null);
